import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import PrismButton from './button.gts';

interface PrismModalSignature {
  Args: {
    isCloseDisabled?: boolean;
    onClose?: () => void;
    title?: string;
  };
  Blocks: {
    actions: [];
    default: [];
    footer: [];
    headerContent: [];
    navigation: [];
  };
  Element: HTMLDivElement;
}

export default class PrismModal extends Component<PrismModalSignature> {
  @tracked isClosing = false;

  get destinationElement(): HTMLElement {
    const wormhole = document.getElementById('prism-wormhole');

    if (!wormhole) {
      throw new Error(
        'Prism::Modal component requires a Prism::Wormhole component to be present in the DOM',
      );
    }

    return wormhole;
  }

  @action
  handleClose(): void {
    // Extra safety to make sure we only emit the closeModal event once
    if (this.isClosing) {
      return;
    }

    this.isClosing = true;

    const { onClose } = this.args;

    if (!onClose) {
      return;
    }

    setTimeout(() => onClose(), 500);
  }

  @action
  onOverlayClick(): void {
    if (this.args.isCloseDisabled) {
      return;
    }

    this.handleClose();
  }

  <template>
    {{! template-lint-disable no-bare-strings no-invalid-interactive }}
    {{#in-element this.destinationElement insertBefore=null}}
      <div
        class="prism-modal-container
          {{if this.isClosing 'fade-out-animation' 'fade-in-animation'}}"
        data-test-id="prism-modal"
        ...attributes
      >
        <div
          class="overlay"
          data-test-id="prism-modal-overlay"
          {{on "click" this.onOverlayClick}}
        />

        <div class="modal">
          <div class="modal__header">
            {{#if (has-block "navigation")}}
              <div class="modal__header__navigation">
                {{yield to="navigation"}}
              </div>
            {{/if}}

            {{#if (has-block "headerContent")}}
              {{yield to="headerContent"}}
            {{/if}}

            {{#if @title}}
              <h3 class="modal__header__title" data-test-id="prism-modal-title">
                {{@title}}
              </h3>
            {{/if}}

            <div class="modal__header__actions">
              {{#if (has-block "actions")}}
                <div class="extra-actions">
                  {{yield to="actions"}}
                </div>
              {{/if}}

              <PrismButton
                @buttonType="ghost"
                @icon="cross"
                @isDisabled={{@isCloseDisabled}}
                @onClick={{this.handleClose}}
                aria-label="Close modal"
                data-test-id="close-modal-button"
              />
            </div>
          </div>

          <div class="modal__content" data-test-id="prism-modal-content">
            {{yield}}
          </div>

          {{#if (has-block "footer")}}
            <div class="modal__footer">
              {{yield to="footer"}}
            </div>
          {{/if}}
        </div>
      </div>
    {{/in-element}}
  </template>
}
